import React, { useState } from 'react';
import { getProjectScopeFileUrl } from '../../../services/projectService';
import styles from './ScopeViewer.module.css';

/**
 * SourceDocumentLink - opens the scope document the scope was extracted from.
 * The file lives in a private bucket, so the URL is signed on demand.
 */
const SourceDocumentLink = ({ projectId, fileName }) => {
  const [error, setError] = useState('');

  const handleOpen = async () => {
    setError('');
    try {
      const url = await getProjectScopeFileUrl(projectId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not open the source document');
    }
  };

  return (
    <div className={styles.sourceDoc}>
      <button type="button" className={styles.sourceDocLink} onClick={handleOpen}>
        View source document: {fileName}
      </button>
      {error && <span className={styles.sourceDocError}>{error}</span>}
    </div>
  );
};

/**
 * ScopeViewer - Read-only viewer for structured project scope data.
 *
 * Tries to JSON.parse the scope string. If it's valid structured JSON,
 * renders each section with its fields. If it's plain text (old format),
 * falls back to pre-formatted text display.
 *
 * When `projectId` and `fileName` are given, also links to the uploaded
 * document the scope was extracted from.
 */
const ScopeViewer = ({ scopeData, projectId, fileName }) => {
  if (!scopeData) return null;

  // Try to parse as structured JSON
  let parsed = null;
  try {
    parsed = JSON.parse(scopeData);
  } catch {
    // Not JSON — render as plain text (backwards compatibility)
    return (
      <div className={styles.plainText}>
        <pre>{scopeData}</pre>
      </div>
    );
  }

  // If parsed but doesn't look like our structured format, show as plain text
  if (!parsed || typeof parsed !== 'object') {
    return (
      <div className={styles.plainText}>
        <pre>{scopeData}</pre>
      </div>
    );
  }

  const renderValue = (value, field) => {
    if (value === null || value === undefined || value === '') {
      return <span className={styles.emptyValue}>Not provided</span>;
    }

    // Arrays (multi-select tags / checkboxes)
    if (Array.isArray(value)) {
      if (value.length === 0) return <span className={styles.emptyValue}>None selected</span>;
      return (
        <div className={styles.tagsList}>
          {value.map((v, i) => (
            <span key={i} className={styles.viewTag}>{v}</span>
          ))}
        </div>
      );
    }

    // Editable checklist objects
    if (typeof value === 'object' && (value.items || value.customItems)) {
      const allItems = [
        ...(value.items || []),
        ...(value.customItems || [])
      ];
      if (allItems.length === 0) return <span className={styles.emptyValue}>No items</span>;
      return (
        <ul className={styles.checklistView}>
          {allItems.map((item, i) => (
            <li key={i} className={item.checked ? styles.checkedItem : styles.uncheckedItem}>
              <span className={styles.checkIcon}>{item.checked ? 'Yes' : 'No'}</span>
              <span>{item.text}</span>
            </li>
          ))}
        </ul>
      );
    }

    // Plain string/number
    return <span className={styles.fieldValue}>{String(value)}</span>;
  };

  const renderSection = (sectionName, sectionData) => {
    // Repeatable sections (arrays of rows)
    if (Array.isArray(sectionData)) {
      if (sectionData.length === 0) {
        return <span className={styles.emptyValue}>No entries</span>;
      }

      return (
        <div className={styles.repeatableView}>
          {sectionData.map((row, rowIndex) => (
            <div key={rowIndex} className={styles.repeatableRow}>
              <span className={styles.rowNumber}>#{rowIndex + 1}</span>
              <div className={styles.rowFields}>
                {Object.entries(row).map(([key, val]) => (
                  <div key={key} className={styles.viewField}>
                    <span className={styles.viewLabel}>{key}</span>
                    {renderValue(val)}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }

    // Regular sections (object of key-value fields)
    if (typeof sectionData === 'object') {
      return (
        <div className={styles.viewFields}>
          {Object.entries(sectionData).map(([key, val]) => (
            <div key={key} className={styles.viewField}>
              <span className={styles.viewLabel}>{key}</span>
              {renderValue(val)}
            </div>
          ))}
        </div>
      );
    }

    return <span className={styles.fieldValue}>{String(sectionData)}</span>;
  };

  return (
    <div className={styles.scopeViewer}>
      {projectId && fileName && <SourceDocumentLink projectId={projectId} fileName={fileName} />}
      {Object.entries(parsed).map(([sectionName, sectionData]) => (
        <div key={sectionName} className={styles.viewSection}>
          <h4 className={styles.viewSectionTitle}>{sectionName}</h4>
          {renderSection(sectionName, sectionData)}
        </div>
      ))}
    </div>
  );
};

export default ScopeViewer;
